<template>
  <div>
    <v-dialog
      v-model="internalShowDialog"
      :max-width="dialogMaxWidth"
      :persistent="persistent"
      class="export-columns-dialog"
    >
      <v-card class="export-columns">
        <div class="flex justify-between items-center btnClass border-b p-4">
          <div class="flex flex-row items-center">
            <h6 class="text-xl mb-0">{{ dialogTitle }}</h6>
          </div>
          <v-btn icon @click="cancelExportDialog" color="black">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text>
          <!-- First part: Available fields with checkboxes -->
          <div
            class="border-r"
            :style="{ width: availableFieldsWidth, display: 'inline-block', verticalAlign: 'top' }"
          >
            <div class="flex pl-2 items-center mb-2">
              <v-checkbox
                small
                color="primary"
                :label="availableFieldsTitle"
                hide-details
                @click="selectAllFields"
                :disabled="!exportKeys?.length"
                :input-value="allItemsSelected"
                :indeterminate="indeterminate"
              >
              </v-checkbox>
            </div>
            <v-divider></v-divider>
            <div
              class="available-keys-list"
              :style="{ maxHeight: availableFieldsMaxHeight, overflowY: 'auto' }"
            >
              <v-checkbox
                v-for="(key, index) in exportKeys"
                :key="getKey(key, index)"
                v-model="internalSelectedKeys"
                :label="getKeyLabel(key)"
                :value="getKeyValue(key)"
                v-tooltip="{ text: getKeyLabel(key), disabled: getKeyLabel(key).length <= 20 }"
                dense
                hide-details
                class="list-item"
              ></v-checkbox>
              <div v-if="!exportKeys.length" class="text--secondary text-center py-4">
                {{ noFieldsMessage }}
              </div>
            </div>
          </div>

          <!-- Second part: Table for selected fields -->
          <div
            class="pl-4"
            :style="{ width: tableWidth, display: 'inline-block', verticalAlign: 'top' }"
          >
            <div
              v-if="exportTableHeader.length === 0"
              class="flex justify-center items-center py-6"
            >
              <div class="text-center">
                <v-icon size="32" color="grey lighten-1 mb-1">mdi-table-large</v-icon>
                <p class="text-subtitle-1 font-weight-medium mb-0" style="line-height: 1.2">
                  {{ noFieldsSelectedMessage }}
                </p>
                <p class="text-caption grey--text mb-0 mt-1">
                  {{ selectFieldsInstruction }}
                </p>
              </div>
            </div>
            <div v-else class="table-container">
              <!-- Show no-data-wrapper outside table when there are no columns -->

              <!-- Show table when there are columns -->
              <v-data-table
                :headers="exportTableHeader"
                hide-default-footer
                disable-sort
                :items="[]"
                class="sortHandle"
                fixed-header
                :key="anIncreasingNumber"
                v-sortable-table="{ onEnd: sortTheHeadersAndUpdateTheKey }"
              >
                <template
                  v-for="(header, index) in exportTableHeader"
                  v-slot:[`header.${header.text}`]
                >
                  <div
                    :key="index"
                    class="font-title header-text"
                    v-tooltip="{ text: header.text, disabled: header.text.length <= 20 }"
                  >
                    <v-icon class="drag-icon" small>mdi-drag</v-icon>
                    {{ header.text }}
                  </div>
                </template>
                <template>
                  <div
                    v-if="exportTableHeader.length > 0 && showInstructions"
                    class="text-center flex items-center justify-center mt-2"
                  >
                    <p class="mb-0">{{ instructionsText }}</p>
                  </div>
                </template>
              </v-data-table>
            </div>
            <div>
              <div
                v-if="showInstructions"
                class="text-center flex items-center justify-center mt-4 mb-2"
              >
                <p class="text-lg text-gray-500 mb-0">{{ instructionsText }}</p>
              </div>

              <!-- Saved Templates -->
              <div v-if="identifier && useLocalStorage" class="mt-4 px-4 pb-4 border-t pt-4">
                <div class="text-sm font-bold mb-3 text-gray-700">Saved Formats (Max 4)</div>

                <div class="d-flex flex-wrap" style="gap: 12px">
                  <v-card
                    v-for="(template, idx) in savedTemplates"
                    :key="idx"
                    outlined
                    class="d-flex flex-column px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                    :style="{
                      borderRadius: '8px',
                      width: '200px',
                      borderColor: isTemplateSelected(template) ? '#1867c0' : '#e0e0e0',
                      borderWidth: isTemplateSelected(template) ? '2px' : '1px',
                      backgroundColor: isTemplateSelected(template) ? '#f0f7ff' : '#ffffff'
                    }"
                    @click="applyTemplate(template)"
                  >
                    <div class="d-flex align-center justify-space-between w-full mb-1">
                      <div class="d-flex align-center" style="max-width: 80%">
                        <v-icon small color="primary" class="mr-1"
                          >mdi-file-document-outline</v-icon
                        >
                        <span
                          class="text-truncate font-weight-medium text-body-2"
                          :title="template.name"
                          style="color: #424242"
                        >
                          {{ template.name }}
                        </span>
                      </div>
                      <v-btn
                        icon
                        small
                        class="ml-1"
                        @click.stop="deleteTemplate(idx)"
                        style="height: 24px; width: 24px"
                      >
                        <v-icon small color="red lighten-1">mdi-close</v-icon>
                      </v-btn>
                    </div>

                    <div class="text-caption text-gray-500 mb-2 mt-1 font-weight-bold">
                      {{ template.selectedKeys.length }} columns selected
                    </div>

                    <div
                      class="text-xs text-gray-500 overflow-y-auto pr-1 custom-scroll"
                      style="
                        height: 60px;
                        border-top: 1px dashed #e0e0e0;
                        padding-top: 6px;
                        line-height: 1.2;
                      "
                    >
                      {{ getTemplateColumnNames(template).join(', ') }}
                    </div>
                  </v-card>

                  <div
                    v-if="savedTemplates.length === 0"
                    class="text-caption text-gray-500 font-italic d-flex align-center"
                  >
                    No saved formats yet. Formats save automatically upon download.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <v-card-actions class="flex justify-center mb-2 border-t pt-4">
            <v-spacer></v-spacer>
            <v-btn outlined color="grey" class="rounded-md mr-4 px-6" @click="cancelExportDialog">
              <span class="text-black dark-text-color normal-case">{{ cancelBtnText }}</span>
            </v-btn>
            <v-btn
              outlined
              color="green"
              class="rounded-md px-6 normal-case mr-4"
              @click="openSaveDraftDialog"
              :disabled="exportLoading || !internalSelectedKeys?.length"
            >
              <v-icon left>mdi-content-save-outline</v-icon>
              Save Draft
            </v-btn>
            <v-btn
              :color="exportBtnColor"
              class="white--text rounded-md px-6 normal-case"
              @click="exportData"
              :loading="exportLoading"
              :disabled="exportLoading || !internalSelectedKeys?.length"
            >
              <v-icon left dark>mdi-download</v-icon>
              {{ exportBtnText }}
            </v-btn>
          </v-card-actions>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Save Draft Dialog -->
    <v-dialog v-model="saveDraftDialog" max-width="450" persistent>
      <v-card class="pa-4">
        <v-card-title class="text-h6 font-weight-bold pb-2">Save Format as Draft</v-card-title>
        <v-divider class="mb-2"></v-divider>
        <v-card-text class="pt-2">
          <p class="mb-4 text-gray-600 text-body-2">
            This will save your current column arrangement for future re-use. You can save up to 4
            templates. Once saved, the file will automatically start downloading.
          </p>
          <v-text-field
            v-model="templateName"
            label="Format Name"
            placeholder="e.g. My Custom Report"
            dense
            outlined
            hide-details
            autofocus
            color="primary"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="px-0 pt-4">
          <v-spacer></v-spacer>
          <v-btn outlined class="normal-case rounded" @click="saveDraftDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="primary" class="white--text normal-case rounded" @click="confirmSaveDraft"
            >Save & Download</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Sortable from 'sortablejs'

function watchClass(targetNode, classToWatch) {
  let lastClassState = targetNode.classList.contains(classToWatch)
  const observer = new MutationObserver((mutationsList) => {
    for (let mutation of mutationsList) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const currentClassState = mutation.target.classList.contains(classToWatch)
        if (lastClassState !== currentClassState) {
          lastClassState = currentClassState
          if (!currentClassState) {
            mutation.target.classList.add('sortHandle')
          }
        }
      }
    }
  })
  observer.observe(targetNode, { attributes: true })
}

export default {
  name: 'GenericExportColumns',
  data() {
    return {
      userOrg: null,
      anIncreasingNumber: 0,
      internalSelectedKeys: [],
      internalShowDialog: false,
      saveDraftDialog: false,
      templateName: '',
      savedTemplates: []
    }
  },
  props: {
    // Dialog properties
    showDialog: {
      type: Boolean,
      default: false
    },
    dialogMaxWidth: {
      type: String,
      default: '83rem'
    },
    persistent: {
      type: Boolean,
      default: true
    },

    // Titles and labels
    dialogTitle: {
      type: String,
      default: 'Manage Export Columns'
    },
    availableFieldsTitle: {
      type: String,
      default: 'Available Fields'
    },

    cancelBtnText: {
      type: String,
      default: 'Cancel'
    },
    exportBtnText: {
      type: String,
      default: 'Export'
    },

    // Instructions
    showInstructions: {
      type: Boolean,
      default: true
    },
    instructionsText: {
      type: String,
      default: '← Drag to rearrange headers →'
    },

    // Messages
    noFieldsMessage: {
      type: String,
      default: 'No fields available'
    },
    noFieldsSelectedMessage: {
      type: String,
      default: 'No fields selected'
    },
    selectFieldsInstruction: {
      type: String,
      default: 'Select fields from the left panel to begin'
    },

    // Layout
    availableFieldsWidth: {
      type: String,
      default: '15%'
    },
    tableWidth: {
      type: String,
      default: '85%'
    },
    availableFieldsMaxHeight: {
      type: String,
      default: '465px'
    },

    // Data
    exportKeys: {
      type: Array,
      default: () => []
    },
    exportLoading: {
      type: Boolean,
      default: false
    },
    identifier: {
      type: String,
      default: ''
    },
    exportBtnColor: {
      type: String,
      default: 'primary'
    },

    // Key mapping
    keyLabelProperty: {
      type: String,
      default: 'text'
    },
    keyValueProperty: {
      type: String,
      default: 'value'
    },
    keyUniqueProperty: {
      type: String,
      default: 'value'
    },

    // Storage
    useLocalStorage: {
      type: Boolean,
      default: true
    },
    storageKeyName: {
      type: String,
      default: 'exportColumnSelectedKey'
    },

    // Default selections
    defaultSelectedKeys: {
      type: Array,
      default: () => []
    }
  },
  directives: {
    'sortable-table': {
      inserted: (el, binding) => {
        el.querySelectorAll('th').forEach((draggableEl) => {
          watchClass(draggableEl, 'sortHandle')
          draggableEl.classList.add('sortHandle')
        })
        Sortable.create(
          el.querySelector('tr'),
          binding.value ? { ...binding.value, handle: '.sortHandle' } : {}
        )
      }
    }
  },
  watch: {
    internalSelectedKeys: {
      handler(newHeaders, oldHeaders) {
        // Only update if there's a real change in the array contents
        if (JSON.stringify(newHeaders) !== JSON.stringify(oldHeaders)) {
          // Just increment the key to refresh the table
          this.$nextTick(() => {
            this.anIncreasingNumber += 1
          })
        }
      },
      deep: true
    },
    identifier: {
      handler(newVal) {
        if (newVal && this.useLocalStorage) {
          this.loadFromStorage(newVal)
        }
      }
    },
    showDialog: {
      handler(newVal) {
        this.internalShowDialog = newVal
        if (newVal && this.identifier && this.useLocalStorage) {
          this.loadFromStorage(this.identifier)
        }
      },
      immediate: true
    },
    internalShowDialog: {
      handler(newVal) {
        this.$emit('update:showDialog', newVal)
        // Load selections when dialog opens
        if (newVal && this.identifier && this.useLocalStorage) {
          this.loadFromStorage(this.identifier)
        }
        // Don't clear selections when dialog is closed
        // if (!newVal) {
        //   this.internalSelectedKeys = []
        // }
      }
    }
  },
  computed: {
    exportTableHeader() {
      const headers = this.internalSelectedKeys.map((keyValue) => {
        const fullHeader = this.exportKeys.find((header) => {
          const headerValue = typeof header === 'object' ? header[this.keyValueProperty] : header
          return headerValue === keyValue
        })
        if (fullHeader && typeof fullHeader === 'object') {
          return {
            text: fullHeader[this.keyLabelProperty],
            value: fullHeader[this.keyValueProperty],
            sortable: false,
            width: '120px'
          }
        }
        return {
          text: keyValue,
          value: keyValue,
          sortable: false,
          width: '120px'
        }
      })
      return headers
    },
    allItemsSelected() {
      return (
        this.internalSelectedKeys.length > 0 &&
        this.internalSelectedKeys.length === this.exportKeys.length
      )
    },
    indeterminate() {
      return (
        this.internalSelectedKeys.length > 0 &&
        this.internalSelectedKeys.length < this.exportKeys.length
      )
    }
  },
  methods: {
    getKey(key, index) {
      if (typeof key === 'object' && key[this.keyUniqueProperty] !== undefined) {
        return key[this.keyUniqueProperty]
      }
      return index
    },
    getKeyLabel(key) {
      if (typeof key === 'object' && key[this.keyLabelProperty] !== undefined) {
        return key[this.keyLabelProperty]
      }
      return key
    },
    getKeyValue(key) {
      if (typeof key === 'object' && key[this.keyValueProperty] !== undefined) {
        return key[this.keyValueProperty]
      }
      return key
    },
    sortTheHeadersAndUpdateTheKey(evt) {
      let headersTmp = [...this.exportTableHeader]
      const oldIndex = evt.oldIndex
      const newIndex = evt.newIndex

      if (newIndex >= headersTmp.length) {
        let k = newIndex - headersTmp.length + 1
        while (k--) {
          headersTmp.push(undefined)
        }
      }

      headersTmp.splice(newIndex, 0, headersTmp.splice(oldIndex, 1)[0])
      headersTmp = headersTmp.filter((el) => el !== undefined)
      const newKeys = headersTmp.map((key) => key?.value)
      this.internalSelectedKeys.splice(0, this.internalSelectedKeys.length, ...newKeys)
      this.anIncreasingNumber += 1
      if (this.identifier && this.useLocalStorage) {
        this.saveToStorage()
      }
    },
    cancelExportDialog() {
      this.internalShowDialog = false
      // Don't clear selections when closing dialog to preserve user choices
      // this.internalSelectedKeys = []
      this.$emit('closeExportDialog')
      this.$emit('update:showDialog', false)
    },
    exportData() {
      // Every time download, create new template automatically if not exists
      if (this.identifier && this.useLocalStorage) {
        this.saveCurrentAsTemplate()
      }
      this.$emit('exportData', this.internalSelectedKeys)
      if (this.identifier && this.useLocalStorage) {
        this.saveToStorage()
      }
    },
    openSaveDraftDialog() {
      this.templateName = ''
      this.saveDraftDialog = true
    },
    confirmSaveDraft() {
      this.saveDraftDialog = false
      if (this.identifier && this.useLocalStorage) {
        this.saveCurrentAsTemplate()
      }
      this.exportData()
    },
    saveCurrentAsTemplate() {
      if (!this.internalSelectedKeys || !this.internalSelectedKeys.length) return

      const exists = this.savedTemplates.find(
        (t) => JSON.stringify(t.selectedKeys) === JSON.stringify(this.internalSelectedKeys)
      )

      if (!exists) {
        let nameToSave = this.templateName.trim()
        if (!nameToSave) {
          nameToSave = `Format ${this.savedTemplates.length + 1}`
        }

        let dupCount = 1
        let uniqueName = nameToSave
        // Prevent duplicate names
        while (this.savedTemplates.find((t) => t.name === uniqueName)) {
          uniqueName = `${nameToSave} (${dupCount})`
          dupCount++
        }

        this.savedTemplates.unshift({
          name: uniqueName,
          selectedKeys: [...this.internalSelectedKeys],
          timestamp: Date.now()
        })

        // Keep max 4 templates
        if (this.savedTemplates.length > 4) {
          this.savedTemplates.pop()
        }
        this.templateName = ''
      }
    },
    isTemplateSelected(template) {
      if (!this.internalSelectedKeys || !template.selectedKeys) return false
      return JSON.stringify(template.selectedKeys) === JSON.stringify(this.internalSelectedKeys)
    },
    applyTemplate(template) {
      if (template && template.selectedKeys) {
        this.templateName = ''
        this.internalSelectedKeys = [...template.selectedKeys]
        this.$nextTick(() => {
          this.anIncreasingNumber += 1
        })
        if (this.identifier && this.useLocalStorage) {
          this.saveToStorage()
        }
      }
    },
    deleteTemplate(index) {
      this.savedTemplates.splice(index, 1)
      if (this.identifier && this.useLocalStorage) {
        this.saveToStorage()
      }
    },
    getTemplateColumnNames(template) {
      if (!template || !template.selectedKeys) return []
      return template.selectedKeys.map((key) => {
        const field = this.exportKeys.find((k) => this.getKeyValue(k) === key)
        return field ? this.getKeyLabel(field) : key
      })
    },
    loadFromStorage(identifier) {
      if (!this.useLocalStorage) return

      try {
        const storageData = JSON.parse(localStorage.getItem(this.storageKeyName)) || {}
        const loggedInUserId = this.getUserOrgId()

        if (storageData[loggedInUserId]) {
          const entry = storageData[loggedInUserId].find(
            (item) => item.identifier === identifier || item.caseType === identifier
          )
          if (entry) {
            // Only update if we have stored data
            this.savedTemplates = entry.templates || []

            // If there are saved templates available, keep the table empty until user clicks one.
            if (this.savedTemplates.length > 0) {
              this.internalSelectedKeys = []
            } else if (entry.selectedKeys && entry.selectedKeys.length > 0) {
              // Old scenario: no templates, but previously selected keys exist
              // Automatically migrate these legacy keys into a Default Template
              this.savedTemplates = [
                {
                  name: 'Default Template',
                  selectedKeys: [...entry.selectedKeys],
                  timestamp: entry.timestamp || Date.now()
                }
              ]
              this.internalSelectedKeys = []

              // Immediately save this new conversion to local storage
              this.$nextTick(() => {
                if (this.identifier && this.useLocalStorage) {
                  this.saveToStorage()
                }
              })
            } else {
              // Otherwise fallback to empty
              this.internalSelectedKeys = []
            }

            this.$nextTick(() => {
              this.anIncreasingNumber += 1
            })
            return
          }
        }
        // If no stored data is found, select default selections if available, otherwise select nothing
        if (this.defaultSelectedKeys.length > 0) {
          this.internalSelectedKeys = [...this.defaultSelectedKeys]
        } else {
          this.internalSelectedKeys = []
        }
      } catch (e) {
        console.warn('Error loading from storage:', e)
        // Same fallback logic on error
        if (this.defaultSelectedKeys.length > 0) {
          this.internalSelectedKeys = [...this.defaultSelectedKeys]
        } else {
          this.internalSelectedKeys = []
        }
      }
    },
    saveToStorage() {
      if (!this.useLocalStorage || !this.identifier) return

      try {
        const storageData = JSON.parse(localStorage.getItem(this.storageKeyName)) || {}
        const loggedInUserId = this.getUserOrgId()

        if (!storageData[loggedInUserId]) {
          storageData[loggedInUserId] = []
        }

        const existingIndex = storageData[loggedInUserId].findIndex(
          (item) => item.identifier === this.identifier || item.caseType === this.identifier
        )

        const entry = {
          identifier: this.identifier,
          selectedKeys: [...this.internalSelectedKeys],
          templates: this.savedTemplates,
          timestamp: Date.now()
        }

        if (existingIndex >= 0) {
          storageData[loggedInUserId][existingIndex] = entry
        } else {
          storageData[loggedInUserId].push(entry)
        }

        localStorage.setItem(this.storageKeyName, JSON.stringify(storageData))
      } catch (e) {
        console.warn('Error saving to storage:', e)
      }
    },
    getUserOrgId() {
      // Try to get user org from different sources
      if (this.userOrg?._id) return this.userOrg._id
      if (this.$storage?.getUniversal) {
        const user = this.$storage.getUniversal('user')
        return user?._id
      }
      return 'default_user'
    },
    selectAllFields() {
      if (this.allItemsSelected) {
        // If all items are selected, deselect all
        this.internalSelectedKeys = []
      } else {
        // If not all items are selected, select all
        this.internalSelectedKeys = this.exportKeys.map((key) => this.getKeyValue(key))
      }
      this.anIncreasingNumber += 1

      // Save to storage if enabled
      if (this.identifier && this.useLocalStorage) {
        this.saveToStorage()
      }
    },
    openDialog() {
      this.internalShowDialog = true
      this.$emit('update:showDialog', true)

      // Load from storage when opening dialog
      if (this.identifier && this.useLocalStorage) {
        this.loadFromStorage(this.identifier)
      }
    }
  },
  mounted() {
    // Initialize userOrg
    if (this.$storage?.getUniversal) {
      this.userOrg = this.$storage.getUniversal('user')
    }
    // Load stored selections when component is mounted
    if (this.identifier && this.useLocalStorage) {
      this.loadFromStorage(this.identifier)
    }
  },
  beforeDestroy() {
    // Clean up any references
    this.userOrg = null
  }
}
</script>

<style scoped>
.drag-icon {
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  margin-right: 4px;
  cursor: move;
}

.header-text:hover .drag-icon {
  opacity: 1;
}

.border-r {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.available-keys-list {
  min-height: 100px;
}

.v-checkbox {
  margin-top: 0;
  margin-bottom: 0;
}

.table-container {
  /* height: 700px !important; */
  display: flex;
  /* align-items: center; */
  justify-content: center;
}

.full-height-table {
  height: 100% !important;
  width: 100% !important;
}

.no-data-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}

.font-title {
  font-size: 0.875rem;
  color: #000;
  max-width: 120px;
  white-space: nowrap;
  margin-bottom: 0px !important;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.header-text {
  display: block;
  width: 100%;
}
.header-text:hover {
  cursor: move;
}

.list-item {
  padding-top: 0px !important;
  padding: 8px;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.export-columns {
  padding: 0px !important;
}
.export-columns-dialog {
  overflow-y: hidden !important;
}
.Instructions {
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.no-data-content {
  text-align: center;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.custom-scroll::-webkit-scrollbar {
  width: 4px;
}
.custom-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}
.custom-scroll::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}
.custom-scroll::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
